import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    CheckCircle2, Clock, ExternalLink, HandCoins, ShieldAlert, Timer, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SampleBanner } from "@/components/SampleBanner";
import { fetchDeal, reviewDealProof, submitDealProof } from "@/lib/api";
import { money } from "@/lib/format";
import { longDate, timeLeft } from "@/lib/market";
import { readWallet } from "@/lib/market";

const DEAL_META = {
    awaiting_proof: { label: "Awaiting proof", className: "bg-heat/15 text-heat border-heat/40" },
    proof_submitted: { label: "Proof in — review open", className: "bg-heat/15 text-heat border-heat/40" },
    accepted: { label: "Accepted — paid out", className: "bg-brand/15 text-brand border-brand/30" },
    disputed: { label: "Disputed — platform holding", className: "bg-destructive/15 text-destructive border-destructive/40" },
    refunded: { label: "Refunded to winner", className: "bg-secondary text-muted-foreground" },
};

/** Proof room: creator uploads, sponsor accepts or disputes inside 48 hours. */
export default function Proof() {
    const { dealId = "" } = useParams();
    const queryClient = useQueryClient();
    const [wallet] = useState(() => readWallet());
    const [url, setUrl] = useState("");
    const [note, setNote] = useState("");
    const [reviewNote, setReviewNote] = useState("");
    const [busy, setBusy] = useState(false);

    const deal = useQuery({
        queryKey: ["market-deal", dealId],
        queryFn: () => fetchDeal(dealId, wallet),
        retry: false,
        refetchInterval: 20000,
    });

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ["market-deal", dealId] });
        queryClient.invalidateQueries({ queryKey: ["market-listing"] });
    };

    const upload = async () => {
        if (!url.trim() || busy) return;
        setBusy(true);
        try {
            await submitDealProof(dealId, { url: url.trim(), note: note.trim() }, wallet);
            toast.success("Proof submitted — the 48h review window is open");
            setUrl("");
            setNote("");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit proof");
        } finally {
            setBusy(false);
        }
    };

    const review = async (decision) => {
        if (busy) return;
        setBusy(true);
        try {
            await reviewDealProof(dealId, { decision, note: reviewNote.trim() }, wallet);
            toast.success(decision === "accept" ? "Accepted — creator payout released" : "Disputed — funds held by the platform");
            setReviewNote("");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Review failed");
        } finally {
            setBusy(false);
        }
    };

    if (deal.isLoading) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-4">
                <Skeleton className="h-24 w-full rounded-2xl" />
                <Skeleton className="h-40 w-full rounded-2xl" />
                <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
        );
    }

    if (deal.isError || !deal.data?.deal) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
                <h1 className="font-heading text-2xl font-bold">Deal not found</h1>
                <p className="text-muted-foreground mt-2 mb-6">This proof room does not exist.</p>
                <Button asChild className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                    <Link to="/market">Back to the market</Link>
                </Button>
            </div>
        );
    }

    const ctx = deal.data;
    const d = ctx.deal;
    const l = ctx.listing;
    const meta = DEAL_META[d.status] || { label: d.status, className: "bg-secondary text-muted-foreground" };
    const rules = ctx.rules || {};

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <SampleBanner className="mb-6" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                <h1 className="font-heading text-3xl font-bold tracking-tight inline-flex items-center gap-3">
                    <HandCoins className="h-7 w-7 text-brand" /> Proof room
                </h1>
                <Badge className={meta.className} data-testid="deal-status">{meta.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
                <Link to={`/a/${l.id}`} className="hover:text-foreground">{l.title}</Link>
                {" "}· {ctx.creator.name} (@{ctx.creator.handle}) · won by {ctx.wallet_label}
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="rounded-xl border border-border bg-card px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Deal size</div>
                    <div className="font-heading text-xl font-bold tabular">{money(d.amount_usd)}</div>
                </div>
                <div className="rounded-xl border border-border bg-card px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Platform fee 12%</div>
                    <div className="font-heading text-xl font-bold tabular text-heat">{money(d.fee_usd)}</div>
                </div>
                <div className="rounded-xl border border-border bg-card px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Creator payout</div>
                    <div className="font-heading text-xl font-bold tabular text-brand">{money(d.payout_usd)}</div>
                </div>
                <div className="rounded-xl border border-border bg-card px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Closed</div>
                    <div className="font-heading text-sm font-semibold tabular pt-1">{longDate(d.created_at)}</div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="bg-card border-border p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Timer className="h-4 w-4 text-heat" /> Proof window (7 days)
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        Deadline {longDate(d.proof_deadline)} ·{" "}
                        {d.status === "awaiting_proof" ? `${timeLeft(ctx.proof_seconds_left)} left` : "closed"}
                    </p>
                </Card>
                <Card className="bg-card border-border p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Clock className="h-4 w-4 text-heat" /> Review window (48 hours)
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        {d.status === "proof_submitted"
                            ? `${timeLeft(ctx.dispute_seconds_left)} left to accept or dispute`
                            : d.dispute_deadline
                                ? `Was open until ${longDate(d.dispute_deadline)}`
                                : "Opens when proof is submitted"}
                    </p>
                </Card>
            </div>

            <div className="space-y-6">
                {ctx.can_submit_proof ? (
                    <Card className="bg-card border-border p-5" data-testid="proof-upload">
                        <h2 className="font-heading text-lg font-bold mb-1">Upload proof</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            Required by the listing: {l.proof_required || "photos and links of the delivered work"}.
                            Link the post, album or analytics view.
                        </p>
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="proof-url">Proof link</Label>
                                <Input
                                    id="proof-url"
                                    data-testid="proof-url-input"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://instagram.com/p/…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="proof-note">Note (optional)</Label>
                                <Textarea
                                    id="proof-note"
                                    data-testid="proof-note-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={2}
                                    placeholder="Race-day photos + reel analytics"
                                />
                            </div>
                            <Button
                                data-testid="proof-submit-button"
                                onClick={upload}
                                disabled={busy || !url.trim()}
                                className="w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                            >
                                {busy ? "Submitting…" : "Submit proof"}
                            </Button>
                        </div>
                    </Card>
                ) : null}

                {ctx.can_review ? (
                    <Card className="bg-card border-border p-5 glow-brand" data-testid="proof-review">
                        <h2 className="font-heading text-lg font-bold mb-1">Review proof</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            You are the winning wallet. Accept to release the payout, or dispute and the
                            platform holds the funds. {timeLeft(ctx.dispute_seconds_left)} left in the window.
                        </p>
                        <Textarea
                            value={reviewNote}
                            onChange={(e) => setReviewNote(e.target.value)}
                            rows={2}
                            placeholder="Optional note explaining the decision"
                            data-testid="review-note-input"
                        />
                        <div className="flex flex-col sm:flex-row gap-2 mt-3">
                            <Button
                                data-testid="review-accept-button"
                                onClick={() => review("accept")}
                                disabled={busy}
                                className="flex-1 bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                            >
                                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Accept — pay {money(d.payout_usd)}
                            </Button>
                            <Button
                                data-testid="review-dispute-button"
                                onClick={() => review("dispute")}
                                disabled={busy}
                                variant="outline"
                                className="flex-1 border-destructive/50 text-destructive hover:bg-destructive hover:text-primary-foreground font-semibold"
                            >
                                <XCircle className="h-4 w-4 mr-1.5" /> Dispute
                            </Button>
                        </div>
                    </Card>
                ) : null}

                <Card className="bg-card border-border p-5" data-testid="proof-list">
                    <h2 className="font-heading text-lg font-bold mb-3">Proof submitted</h2>
                    {ctx.proofs.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Nothing yet. If nothing arrives within 7 days of close, the winner is refunded
                            automatically.
                        </p>
                    ) : (
                        <div className="divide-y divide-border">
                            {ctx.proofs.map((p) => (
                                <div key={p.id} className="py-3 text-sm">
                                    <div className="flex items-center justify-between gap-2">
                                        <a
                                            href={p.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium hover:text-brand inline-flex items-center gap-1 truncate"
                                        >
                                            {p.url} <ExternalLink className="h-3 w-3 opacity-60" />
                                        </a>
                                        <Badge
                                            variant="outline"
                                            className={`text-[10px] shrink-0 ${
                                                p.status === "accepted"
                                                    ? "bg-brand/15 text-brand border-brand/30"
                                                    : p.status === "disputed"
                                                        ? "bg-destructive/15 text-destructive border-destructive/40"
                                                        : "bg-secondary text-muted-foreground"
                                            }`}
                                        >
                                            {p.status}
                                        </Badge>
                                    </div>
                                    {p.note ? <p className="text-xs text-muted-foreground mt-1">{p.note}</p> : null}
                                    {p.review_note ? <p className="text-xs mt-1">Review note: {p.review_note}</p> : null}
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Submitted {longDate(p.submitted_at)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="bg-card border-border p-4">
                    <h3 className="text-sm font-semibold mb-2 inline-flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-brand" /> What happens next
                    </h3>
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                        <li>· Proof inside the 7-day window keeps the deal alive; the winner's funds stay captured.</li>
                        <li>· Sponsor accepts within 48h → creator receives {money(d.payout_usd)} (platform fee {money(d.fee_usd)} at 12%).</li>
                        <li>· Sponsor disputes → funds stay with the platform until the dispute resolves.</li>
                        <li>· No proof in 7 days → {money(d.amount_usd)} refunds to {ctx.wallet_label} automatically.</li>
                    </ul>
                </Card>
            </div>
        </div>
    );
}



